import {
  createElement,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';
import Markdown from 'react-markdown';
import clsx from 'clsx';
import { cn } from '../../../lib/utils.ts';
import { useImageLoader } from '../../../hooks/useImageLoader.ts';
import {
  hasMeaningfulSlideMarkdown,
  isImageOnlySlide,
  normalizeSlideImageTilt,
  slideContentTiltAnimationClass,
  slideImageTiltAnimationClass,
  type SlideImageTiltStyles,
} from '../slideLayout.ts';
import { useStageImageOnlyFitSquare } from '../useStageImageOnlySafeArea.ts';
import { isFreeformLayout } from '../slideFrame.ts';
import {
  FreeformSlideFrame,
  type FreeformSlideData,
} from '../freeform/FreeformSlideFrame.ts';
import type { SlideThemeFonts } from '../slideFrame.ts';

export type StageSlideCanvasStyles = SlideImageTiltStyles & {
  contentCard: string;
  imageOnlySafeArea: string;
  imageOnlyFit: string;
  fullBleedImage: string;
  framedImage: string;
};

/**
 * Slide payload the current Stage view reads. Extra FREEFORM fields are
 * ignored so already-open Stages keep a blank theme frame.
 */
export type StageSlideCanvasData = {
  title?: string | null;
  content?: string | null;
  optionalImageUrl?: string | null;
  imageTilt?: string | null;
  layout?: string | null;
  showQr?: boolean | null;
  background?: FreeformSlideData['background'];
  elements?: FreeformSlideData['elements'];
};

export function StageSlideCanvas({
  currentSlideId,
  slideData,
  styles,
  themeFonts,
  children,
}: {
  currentSlideId: string | null;
  slideData: StageSlideCanvasData | null;
  styles: StageSlideCanvasStyles;
  themeFonts?: SlideThemeFonts | null;
  children?: ReactNode;
}): ReactElement {
  const content =
    typeof slideData?.content === 'string' ? slideData.content : '';
  const title = typeof slideData?.title === 'string' ? slideData.title : '';
  const imageUrl = slideData?.optionalImageUrl || null;
  const imageTilt = normalizeSlideImageTilt(slideData?.imageTilt);
  const freeform = isFreeformLayout(slideData?.layout);
  const showMarkdown = !freeform && hasMeaningfulSlideMarkdown(content, title);
  const imageOnly = !freeform && isImageOnlySlide({ content, imageUrl, title });

  const { imageLoaded } = useImageLoader({ url: imageUrl });
  const { containerRef, fitSide } = useStageImageOnlyFitSquare(imageOnly);
  const contentReady = imageLoaded || !imageUrl;

  const imageTiltClass = imageLoaded
    ? slideImageTiltAnimationClass({
        tilt: imageTilt,
        imageOnly,
        styles,
      })
    : undefined;

  const contentTiltClass = contentReady
    ? slideContentTiltAnimationClass({
        tilt: imageTilt,
        styles,
      })
    : undefined;

  const fitStyle: CSSProperties | undefined = fitSide
    ? { width: fitSide, height: fitSide }
    : undefined;

  return createElement(
    'div',
    {
      ref: containerRef,
      'data-name': 'SLIDE_STAGE_FRAME',
      className: cn(
        'relative flex h-full min-h-0 w-full items-center justify-center',
        freeform ? 'overflow-visible' : 'overflow-hidden',
      ),
    },
    !currentSlideId
      ? createElement(
          'div',
          { className: 'absolute inset-0 flex items-center justify-center' },
          createElement(
            'div',
            {
              className:
                'font-screen-body text-2xl font-bold text-screen-foreground',
            },
            'No slide selected',
          ),
        )
      : null,
    currentSlideId && freeform
      ? createElement(FreeformSlideFrame, {
          key: currentSlideId,
          slideId: currentSlideId,
          slideData,
          entranceClassName: styles.enterFlat,
          fill: 'window',
          themeFonts,
        })
      : null,
    showMarkdown
      ? createElement(
          Markdown,
          {
            key: currentSlideId,
            className: cn(
              styles.contentCard,
              contentTiltClass,
              !contentReady && 'opacity-0',
            ),
          },
          content,
        )
      : null,
    !freeform && imageUrl && imageOnly
      ? createElement(
          'div',
          {
            'data-name': 'SLIDE_IMAGE_ONLY_SAFE_AREA',
            className: styles.imageOnlySafeArea,
          },
          createElement(
            'div',
            {
              'data-name': 'SLIDE_IMAGE_ONLY_FIT',
              className: styles.imageOnlyFit,
              style: fitStyle,
            },
            createElement('img', {
              key: currentSlideId,
              'data-name': 'SLIDE_FULL_BLEED_IMAGE',
              className: clsx(
                imageLoaded ? undefined : 'opacity-0',
                styles.fullBleedImage,
                imageTiltClass,
              ),
              src: imageUrl,
              alt: 'Slide Image',
            }),
          ),
        )
      : !freeform && imageUrl
        ? createElement('img', {
            key: currentSlideId,
            'data-name': 'SLIDE_FRAMED_IMAGE',
            className: clsx(
              imageLoaded ? undefined : 'opacity-0',
              styles.framedImage,
              imageTiltClass,
            ),
            src: imageUrl,
            alt: 'Slide Image',
          })
        : null,
    children,
  );
}
